import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom-v5-compat';
import {
  selectHydrated,
  hydrateFormData,
  clearFormData,
  loadFormDataFromStorage,
} from '../redux/slices/formSlice';
import { findMissingField, findRouteForField } from '../utils/navigation';
import { FLOW_TYPES, URLS } from '../utils/constants';
import {
  buildInvitationSearch,
  hasCompleteInvitation,
  parseInvitationFromSearch,
} from '../utils/invitationQuery';

/**
 * HOC that ensures required form data fields are available.
 * Attempts to hydrate from sessionStorage if Redux state is empty.
 * Redirects to the appropriate route if required data is missing.
 *
 * @param {React.Component} Component - The component to wrap
 * @param {string[]} requiredFields - Array of required field names
 */
const withFormData = (Component, requiredFields = []) => {
  return props => {
    const store = useStore();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const hydrated = useSelector(selectHydrated);
    const formState = useSelector(state => state.vassForm);
    const [isHydrating, setIsHydrating] = useState(!hydrated);

    // Check if all required fields have valid data
    const missingField = useMemo(
      () => findMissingField(requiredFields, formState),
      [formState],
    );
    const hasAllRequiredData = missingField === null;

    // Attempt to hydrate from sessionStorage on mount
    useEffect(() => {
      if (!hydrated && !hasAllRequiredData) {
        const savedData = loadFormDataFromStorage();
        if (savedData) {
          dispatch(hydrateFormData(savedData));
        }
        setIsHydrating(false);
      } else {
        setIsHydrating(false);
      }
    }, [hydrated, hasAllRequiredData, dispatch]);

    useEffect(() => {
      // Wait for hydration to complete before redirecting
      if (isHydrating) return;

      if (!hasAllRequiredData) {
        // Find the route that sets the missing field
        let redirectPath = findRouteForField(missingField);

        // If redirecting to Verify, clear form and pass invitation only when complete
        if (redirectPath === URLS.VERIFY) {
          const form = store.getState().vassForm;
          const fromUrl = parseInvitationFromSearch(location.search);
          const currentUUID = form.uuid ?? fromUrl.uuid;
          const currentEmail =
            form.veteranContactEmail ?? fromUrl.veteranContactEmail;
          const currentData = form.invitationData ?? fromUrl.data;
          dispatch(clearFormData());
          if (
            hasCompleteInvitation({
              uuid: currentUUID,
              veteranContactEmail: currentEmail,
              data: currentData,
            })
          ) {
            redirectPath = `${URLS.VERIFY}${buildInvitationSearch({
              uuid: currentUUID,
              veteranContactEmail: currentEmail,
              data: currentData,
              cancel: form.flowType === FLOW_TYPES.CANCEL,
            })}`;
          }
        }

        // Navigating to Verify without invitation params shows Something went wrong.
        navigate(redirectPath, {
          replace: true,
        });
      }
    }, [
      hasAllRequiredData,
      missingField,
      navigate,
      isHydrating,
      dispatch,
      store,
      location.search,
    ]);

    // Don't render content until hydration is complete and we have all required data
    if (isHydrating || !hasAllRequiredData) {
      return null;
    }

    return <Component {...props} />;
  };
};

export default withFormData;
